import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { LANGUAGES } from '../i18n';
import useActivityTracker from '../hooks/useActivityTracker';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, isAdmin, isPending, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const isRtl = i18n.language === 'ar';

  useActivityTracker();

  useEffect(() => {
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language, isRtl]);

  const handleLogout = () => { logout(); navigate('/'); setMobileOpen(false); };

  const getDisplayName = () => {
    if (!user) return '';
    let name = '';
    if (user.academic_title && user.academic_title !== 'Non disclosed') name += user.academic_title + ' ';
    name += user.name;
    return name;
  };

  const isActive = (path) => location.pathname === path;

  const navLink = (to, labelKey, testId) => (
    <Link to={to} className={isActive(to) ? 'nav-active' : ''} data-testid={testId} onClick={() => setMobileOpen(false)}>
      {t(`nav.${labelKey}`)}
    </Link>
  );

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  return (
    <div className={isRtl ? 'rtl' : ''}>
      <header className="site-header" data-testid="site-header">
        <div className="container nav-wrap">
          <Link className="logo-link" to="/" data-testid="logo-link" onClick={() => setMobileOpen(false)}>
            <img src="/afhos-logo.jpg" alt="AFHOS Logo" className="logo-image" />
            <span className="logo-text">AFHOS</span>
          </Link>

          {/* Language switcher */}
          <div className="lang-switcher" data-testid="lang-switcher">
            <button className="lang-btn" onClick={() => setLangOpen(!langOpen)} data-testid="lang-toggle">
              <i className="fas fa-globe"></i> {currentLang.code.toUpperCase()}
            </button>
            {langOpen && (
              <div className="lang-dropdown" data-testid="lang-dropdown">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    className={`lang-option ${i18n.language === lang.code ? 'active' : ''}`}
                    onClick={() => { i18n.changeLanguage(lang.code); setLangOpen(false); }}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn" aria-label="Toggle menu">
            <span className={`hamburger ${mobileOpen ? 'open' : ''}`} />
          </button>

          <nav className={`nav ${mobileOpen ? 'nav-open' : ''}`} data-testid="main-nav">
            {navLink('/', 'home', 'nav-home')}
            {navLink('/about', 'about', 'nav-about')}
            {navLink('/history', 'history', 'nav-history')}
            {navLink('/projects', 'projects', 'nav-projects')}
            {navLink('/news', 'news', 'nav-news')}
            {navLink('/events', 'events', 'nav-events')}
            {navLink('/conferences', 'conferences', 'nav-conferences')}
            {navLink('/partners', 'partners', 'nav-partners')}
            {navLink('/workspace', 'members', 'nav-members')}
            {navLink('/cms', 'documents', 'nav-documents')}
            {navLink('/contact', 'contact', 'nav-contact')}
            <a href="https://webmail.easyhost.be/" target="_blank" rel="noreferrer" data-testid="nav-staff-mail" onClick={() => setMobileOpen(false)}>{t('nav.staffMail')}</a>

            {isAuthenticated ? (
              <div className="user-menu" data-testid="user-menu">
                <Link to="/profile" className="user-name-link" data-testid="user-name-link" title={user.email} onClick={() => setMobileOpen(false)}>
                  {getDisplayName()}
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="btn btn-small admin-btn" data-testid="admin-link" onClick={() => setMobileOpen(false)}>
                    {t('nav.admin')}
                  </Link>
                )}
                <button className="btn btn-small btn-secondary" onClick={handleLogout} data-testid="logout-button">{t('nav.logout')}</button>
              </div>
            ) : (
              <div className="auth-buttons" data-testid="auth-buttons">
                <Link to="/auth" className="btn btn-small" data-testid="auth-button" onClick={() => setMobileOpen(false)}>{t('nav.signIn')}</Link>
              </div>
            )}
          </nav>
        </div>
      </header>

      <main className="container" data-testid="main-content">
        {isAuthenticated && isPending && (
          <div className="pending-banner" data-testid="pending-banner">
            <i className="fas fa-clock"></i> {t('pending.banner')}
          </div>
        )}
        {children}
      </main>

      <footer className="site-footer" data-testid="site-footer">
        <div className="container footer-grid">
          <div>
            <img src="/afhos-logo-clear.jpg" alt="AFHOS Logo" className="footer-logo" />
            <h4>AFHOS</h4>
            <p>African Federation of Hypertension Organisations and Societies</p>
          </div>
          <div>
            <h4>{t('footer.quickLinks')}</h4>
            <ul className="list">
              <li><Link to="/about">{t('nav.about')}</Link></li>
              <li><Link to="/projects">{t('nav.projects')}</Link></li>
              <li><Link to="/news">{t('nav.news')}</Link></li>
              <li><Link to="/events">{t('nav.events')}</Link></li>
              <li><Link to="/conferences">{t('nav.conferences')}</Link></li>
              <li><Link to="/contact">{t('nav.contact')}</Link></li>
            </ul>
          </div>
          <div>
            <h4>{t('footer.forMembers')}</h4>
            <ul className="list">
              <li><Link to="/workspace">{t('nav.members')}</Link></li>
              <li><Link to="/cms">{t('nav.documents')}</Link></li>
              <li><Link to="/partners">{t('nav.partners')}</Link></li>
              <li><a href="https://webmail.easyhost.be/" target="_blank" rel="noreferrer">{t('nav.staffMail')}</a></li>
            </ul>
          </div>
          <div>
            <h4>{t('footer.contact')}</h4>
            <p>Email: info@afhos.africa<br />{t('footer.contactDesc')}</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <h4>{t('nav.admin')}</h4>
            <Link to="/admin" className="footer-admin-btn" data-testid="footer-admin-btn">
              <i className="fas fa-tachometer-alt"></i>
              Admin Dashboard
            </Link>
          </div>
        </div>
        <div className="container copyright">
          &copy; {new Date().getFullYear()} AFHOS. {t('footer.rights')}
        </div>
      </footer>
    </div>
  );
};

export default Layout;
